import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import API from '../lib/api';
import ListingCard from '../components/ListingCard';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { MapPin, Car, Wrench, Search, ArrowRight, TrendingUp, Shield, CheckCircle } from 'lucide-react';

// Ghana cities data
const CITIES = {
  'accra': { name: 'Accra', region: 'Greater Accra', description: 'The capital city and economic hub of Ghana' },
  'kumasi': { name: 'Kumasi', region: 'Ashanti', description: 'The Garden City and cultural capital of Ghana' },
  'tamale': { name: 'Tamale', region: 'Northern', description: 'The fastest-growing city in West Africa' },
  'cape-coast': { name: 'Cape Coast', region: 'Central', description: 'Historic coastal city and tourism hub' },
  'takoradi': { name: 'Takoradi', region: 'Western', description: 'The oil city and major port of Ghana' },
  'tema': { name: 'Tema', region: 'Greater Accra', description: 'Ghana\'s largest seaport city' },
  'ho': { name: 'Ho', region: 'Volta', description: 'The capital of Volta Region' },
  'sunyani': { name: 'Sunyani', region: 'Bono', description: 'The cleanest city in Ghana' },
  'bolgatanga': { name: 'Bolgatanga', region: 'Upper East', description: 'The handicraft capital of Ghana' },
  'koforidua': { name: 'Koforidua', region: 'Eastern', description: 'The capital of Eastern Region' },
};

// Popular car brands in Ghana
const POPULAR_BRANDS = ['Toyota', 'Honda', 'Hyundai', 'Kia', 'Nissan', 'Mercedes-Benz', 'BMW', 'Volkswagen'];

export default function CityLanding() {
  const { citySlug } = useParams();
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState({ total: 0, cars: 0, parts: 0 });
  const [loading, setLoading] = useState(true);
  
  const city = CITIES[citySlug] || { name: citySlug?.replace('-', ' '), region: 'Ghana' };
  const isParts = window.location.pathname.includes('/parts/');
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const listingType = isParts ? 'part' : 'car';
        const res = await API.get('/listings', { 
          params: { 
            location: city.name, 
            listing_type: listingType,
            page: 1, 
            limit: 12 
          } 
        });
        setListings(res.data.listings || []);
        setStats({
          total: res.data.total || res.data.listings?.length || 0,
          cars: res.data.listings?.filter(l => l.listing_type === 'car').length || 0,
          parts: res.data.listings?.filter(l => l.listing_type === 'part').length || 0
        });
      } catch (err) {
        console.error('Failed to fetch listings:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, [citySlug, city.name, isParts]);

  // SEO keywords
  const keywords = isParts
    ? `car spare parts ${city.name}, auto parts ${city.name}, car accessories ${city.name}, buy car parts ${city.name} Ghana`
    : `cars for sale ${city.name}, buy cars ${city.name}, used cars ${city.name}, cheap cars ${city.name}, car dealers ${city.name} Ghana`;

  // Structured data for local business
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": isParts ? `Car Spare Parts in ${city.name}` : `Cars for Sale in ${city.name}`,
    "description": `Find ${isParts ? 'auto spare parts' : 'verified cars'} for sale in ${city.name}, ${city.region}, Ghana. AI-verified listings with secure escrow payments.`,
    "numberOfItems": stats.total,
    "itemListElement": listings.slice(0, 5).map((listing, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "item": {
        "@type": isParts ? "Product" : "Vehicle",
        "name": listing.title,
        "url": `https://autotrustgh.com/listing/${listing.listing_id}`
      }
    }))
  };

  return (
    <main className="min-h-screen bg-background">
      <SEO 
        title={isParts ? `Car Spare Parts in ${city.name}` : `Cars for Sale in ${city.name}, Ghana`}
        description={`Find ${stats.total}+ ${isParts ? 'auto spare parts' : 'verified cars'} for sale in ${city.name}, ${city.region}. AI-verified listings, secure escrow payments, mobile money accepted. Buy with confidence on AutoTrust Ghana.`}
        structuredData={structuredData}
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gold/10 to-background border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span>/</span>
            <Link to="/browse" className="hover:text-gold">Browse</Link>
            <span>/</span>
            <span className="text-foreground">{city.name}</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            {isParts ? (
              <>Car Spare Parts in <span className="text-gold">{city.name}</span></>
            ) : (
              <>Cars for Sale in <span className="text-gold">{city.name}</span></>
            )}
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-2xl mb-6">
            {city.description}. Browse {stats.total}+ {isParts ? 'auto parts' : 'verified vehicles'} from trusted sellers in {city.name}, {city.region}.
          </p>
          
          {/* Quick Stats */}
          <div className="flex flex-wrap gap-4 mb-8">
            <div className="flex items-center gap-2 bg-card border border-border/50 rounded-lg px-4 py-2">
              <Car className="w-5 h-5 text-gold" />
              <span className="font-semibold">{stats.total}</span>
              <span className="text-muted-foreground">{isParts ? 'Parts' : 'Cars'} Available</span>
            </div>
            <div className="flex items-center gap-2 bg-card border border-border/50 rounded-lg px-4 py-2">
              <Shield className="w-5 h-5 text-trust-green" />
              <span className="text-muted-foreground">AI-Verified Listings</span>
            </div>
            <div className="flex items-center gap-2 bg-card border border-border/50 rounded-lg px-4 py-2">
              <CheckCircle className="w-5 h-5 text-blue-500" />
              <span className="text-muted-foreground">Secure Escrow</span>
            </div>
          </div>
          
          {/* Toggle Cars/Parts */}
          <div className="flex gap-3">
            <Link to={`/cars/${citySlug}`}>
              <Button variant={!isParts ? "default" : "outline"} className={!isParts ? "bg-gold text-black" : ""}>
                <Car className="w-4 h-4 mr-2" /> Cars in {city.name}
              </Button>
            </Link>
            <Link to={`/parts/${citySlug}`}>
              <Button variant={isParts ? "default" : "outline"} className={isParts ? "bg-gold text-black" : ""}>
                <Wrench className="w-4 h-4 mr-2" /> Parts in {city.name}
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Listings Grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-semibold">
            {isParts ? 'Spare Parts' : 'Featured Cars'} in {city.name}
          </h2>
          <Link to={`/browse?location=${city.name}&listing_type=${isParts ? 'part' : 'car'}`}>
            <Button variant="outline" className="gap-2">
              View All <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-card border border-border/50 rounded-xl h-72 animate-pulse" />
            ))}
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map(listing => (
              <ListingCard key={listing.listing_id} listing={listing} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-card border border-border/50 rounded-xl">
            <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No {isParts ? 'parts' : 'cars'} found in {city.name}</h3>
            <p className="text-muted-foreground mb-4">Be the first to list in this area!</p>
            <Link to="/sell">
              <Button className="bg-gold text-black">Create Listing</Button>
            </Link>
          </div>
        )}
      </section>
      
      {/* Popular Brands Section (Cars only) */}
      {!isParts && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 border-t border-border/50">
          <h2 className="text-xl font-semibold mb-6">Popular Car Brands in {city.name}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {POPULAR_BRANDS.map(brand => (
              <Link 
                key={brand}
                to={`/browse?make=${brand}&location=${city.name}`}
                className="bg-card border border-border/50 rounded-lg p-4 text-center hover:border-gold/50 transition-colors"
              >
                <span className="font-medium text-sm">{brand}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
      
      {/* Other Cities Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 border-t border-border/50">
        <h2 className="text-xl font-semibold mb-6">
          {isParts ? 'Find Parts in Other Cities' : 'Browse Cars in Other Cities'}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(CITIES).filter(([slug]) => slug !== citySlug).slice(0, 6).map(([slug, c]) => (
            <Link 
              key={slug}
              to={isParts ? `/parts/${slug}` : `/cars/${slug}`}
              className="bg-card border border-border/50 rounded-lg p-4 hover:border-gold/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gold" />
                <span className="font-medium">{c.name}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{c.region}</p>
            </Link>
          ))}
        </div>
      </section>
      
      {/* SEO Content Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 border-t border-border/50">
        <div className="prose prose-invert max-w-none">
          <h2 className="text-xl font-semibold mb-4">
            {isParts ? `Buying Car Spare Parts in ${city.name}` : `Buying a Car in ${city.name}`}
          </h2>
          <p className="text-muted-foreground mb-4">
            {isParts ? (
              `Looking for quality car spare parts in ${city.name}, ${city.region}? AutoTrust Ghana connects you with verified sellers offering genuine auto parts, accessories, and components. From engine parts to brake pads, find everything you need for your vehicle maintenance and repairs.`
            ) : (
              `${city.name} is one of Ghana's most active car markets. Whether you're looking for a reliable family sedan, a rugged SUV for Ghana's roads, or an affordable used car, AutoTrust Ghana helps you find the perfect vehicle. Our AI-verified listings ensure you're getting accurate information about each car.`
            )}
          </p>
          
          <h3 className="text-lg font-semibold mb-3">Why Buy on AutoTrust Ghana?</h3>
          <ul className="text-muted-foreground space-y-2 mb-4">
            <li><strong className="text-foreground">AI-Verified Listings:</strong> Every listing is analyzed for accuracy and potential fraud</li>
            <li><strong className="text-foreground">Secure Escrow Payments:</strong> Your money is protected until you receive your {isParts ? 'parts' : 'car'}</li>
            <li><strong className="text-foreground">Mobile Money Accepted:</strong> Pay with MTN MoMo, Vodafone Cash, or AirtelTigo Money</li>
            <li><strong className="text-foreground">Trust Scores:</strong> See at a glance how trustworthy each listing is</li>
            <li><strong className="text-foreground">In-App Chat:</strong> Communicate safely with sellers without sharing personal contact</li>
          </ul>
          
          <p className="text-muted-foreground text-sm">
            <strong>Keywords:</strong> {keywords}
          </p>
        </div>
      </section>
    </main>
  );
}
